import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { ToastProvider } from './components/Toast';
import { getDocsList, getXrefStats } from './api';
import LoginForm from './auth/LoginForm';
import FloatingFeedbackWidget from './components/FloatingFeedbackWidget';
import './App.css';

// コード分割: 各ページを遅延読み込み
const HomePage = React.lazy(() => import('./pages/HomePage'));
// /crawls と /crawls/schedules は / にリダイレクト (IA 統合 2026-05-20)
const CrawlResultsPage = React.lazy(() => import('./pages/CrawlResultsPage'));
const OrgTreeComparePage = React.lazy(() => import('./pages/OrgTreeComparePage'));
const ManualPage = React.lazy(() => import('./pages/ManualPage'));
const UrlCorrectionPage = React.lazy(() => import('./pages/UrlCorrectionPage'));
const PendingUpdatesPage = React.lazy(() => import('./pages/PendingUpdatesPage'));
const CrawlDiffDetailPage = React.lazy(() => import('./pages/CrawlDiffDetailPage'));
const AuditLogPage = React.lazy(() => import('./pages/AuditLogPage'));
const FeedbackPage = React.lazy(() => import('./pages/FeedbackPage'));
const BudgetPage = React.lazy(() => import('./pages/BudgetPage'));
const ResearcherDedupPage = React.lazy(() => import('./pages/ResearcherDedupPage'));
const MasterBrowsePage = React.lazy(() => import('./pages/MasterBrowsePage'));
const KikanUnmatchPage = React.lazy(() => import('./pages/KikanUnmatchPage'));
const XrefPage = React.lazy(() => import('./pages/XrefPage'));
const XrefMakerStatsPage = React.lazy(() => import('./pages/XrefMakerStatsPage'));
const XrefResearchPage = React.lazy(() => import('./pages/XrefResearchPage'));
const XrefTargetHojinPage = React.lazy(() => import('./pages/XrefTargetHojinPage'));

// 調査担当 (リモートワーカー) が入れる画面。
// API 側 (src/api/auth.py) でも同じ線で止めているが、**ほかの画面を開くと
// 何も出ない真っ白な画面になる**ので、こちらへ送り返す。
const RESEARCHER_HOME = '/xref/research';

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const isResearcher = (user.role || '').toLowerCase() !== 'admin';
  if (isResearcher && location.pathname !== RESEARCHER_HOME) {
    return <Navigate to={RESEARCHER_HOME} replace />;
  }
  return children;
};

// Preserve URL search params when redirecting legacy paths so deep links
// from external places (e.g. /results?kikan_code=X bookmarks) still work.
const RedirectWithSearch = ({ to }) => {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return <Navigate to={`${to}${search}`} replace />;
};

// Navigation items as data so the sidebar can switch between full / minimised
// labels without duplicating the route list. Crawl flow consolidated into
// /crawls (schedule + manual + per-kikan) and /crawl-results (summary +
// detail) so the operator no longer ping-pongs across 4 separate screens.
// IA 統合 (2026-05-20): 3画面→2画面
// - ホーム(/) = 旧 / + 旧 /crawls + 旧 /crawls/schedules を統合した運用ハブ
// - 機関別 結果(/crawl-results) = 機関一覧 + 機関別ボード + ヒートマップ
// /pending-updates は /compare のインライン承認に一本化したためメニューから撤去。
// ルート自体は残す (既存の深いリンク/リダイレクト互換のため)。
const NAV_ITEMS = [
  { to: '/',                   end: true,  icon: '🏠', label: 'ホーム' },
  { to: '/crawl-results',      end: false, icon: '📋', label: '機関別 結果' },
  { to: '/master',             end: false, icon: '🗂', label: 'マスタ検索・編集' },
  // 機関アンマッチ (/kikan_unmatch) は **2026-10-08 に「必要なくなった」** (ユーザー判断)
  // のでメニューから外した。**画面・API・表はそのまま残してある** — 判断画面・
  // 候補検索・書き戻しまで実装済みで、必要になればこの 1 行を戻すだけ。
  // URL を直接開けば今でも使える。夜間の自動実行は beat に登録しておらず、
  // 書き戻しも無効なので、置いておくだけでは何も起きない。
  { to: '/researcher-dedup',   end: false, icon: '👥', label: '研究者重複棚卸' },
  { to: '/budget',             end: false, icon: '💰', label: '予算' },
  { to: '/audit-log',          end: false, icon: '📜', label: 'マスタ変更ログ' },
  { to: '/feedback',           end: false, icon: '💬', label: 'ご意見・要望' },
];

// アンマッチ紐付け (LDBコード付与)。system_settings の xref.enabled が立っていて、
// かつ表が作られているときだけメニューに出す。準備前に押しても何も見えないため。
const XREF_NAV_ITEM = { to: '/xref', end: true, icon: '🔗', label: 'アンマッチ紐付け' };
// 付与実績 (メーカー別) も同じ鍵で出し入れする。作業画面の下に並べる。
const XREF_STATS_NAV_ITEM = { to: '/xref/makers', end: false, icon: '📈', label: 'メーカー別 付与実績' };
// 処理3-1 が取り込む法人の切り替え。付与実績の下に並べる。
const XREF_HOJIN_NAV_ITEM = { to: '/xref/hojin', end: false, icon: '🏢', label: '取り込む法人' };
// アンマッチ調査 (調査担当 / リモートワーカー)。作業画面のすぐ下に並べる。
const XREF_RESEARCH_NAV_ITEM = { to: '/xref/research', end: false, icon: '🔎', label: 'アンマッチ調査' };

// 決まった位置に差し込む。目印の項目が無くなっても落ちないよう、
// 見つからなければ末尾に付ける。
const insertBefore = (items, item, beforeTo) => {
  const i = items.findIndex((x) => x.to === beforeTo);
  if (i < 0) return [...items, item];
  return [...items.slice(0, i), item, ...items.slice(i)];
};

const SIDEBAR_WIDTH_OPEN = 200;
const SIDEBAR_WIDTH_COLLAPSED = 52;
const SIDEBAR_STORAGE_KEY = 'sidebarCollapsed';

// Friendly Japanese titles for the guideline submenu. The backend returns
// raw `.md` filenames (e.g. ARCHITECTURE.md), which read like internal jargon
// in the nav; map them to operator-friendly titles. Unknown filenames fall
// back to the stripped basename.
const DOC_TITLE_MAP = {
  'APPROVAL_PATTERNS.md':     '承認判例（パターン一覧）',
  'ARCHITECTURE.md':          'アーキテクチャ',
  'AWS_DEPLOY_GUIDE.md':      'AWS デプロイ手順',
  'CHANGELOG.md':             '変更履歴',
  'crawl_diff_definitions.md':'差分定義',
  'DATA_FLOW.md':             'データフロー',
  'DATA_FLOW.en.md':          'Data Flow (English)',
  'DEV_SETUP.md':             '開発環境セットアップ',
  'IMPLEMENTATION_STATUS.md': '実装状況',
  'PROGRESS.md':              '進捗管理',
  'USER_MANUAL.md':           'ユーザーマニュアル',
  'WEB_APP_MANUAL.md':        'Webアプリマニュアル',
  'BUDGET_MANUAL.md':         '予算担当者マニュアル',
  'LDB_CODE_LINKAGE.md':      'LDBコード付与 現行仕様と改修方針',
  'LDB担当者_運用マニュアル.md': 'LDB担当者 運用マニュアル',
};
const docTitle = (filename) => DOC_TITLE_MAP[filename] || filename.replace(/\.md$/, '');

// サイドバー → ガイドラインに出す資料。**区分つき・この並び順で**出す。
//
// API はディレクトリ順でファイル名を返すので、以前は毎日使うマニュアルが参考
// 資料の下に沈んでいた。役割ごとに区切り、一行の説明を添える (「ユーザー
// マニュアル」と「Webアプリマニュアル」の違いが名前だけでは分からなかった)。
//
// 技術者向け (ARCHITECTURE / AWS_DEPLOY / DATA_FLOW / DEV_SETUP /
// IMPLEMENTATION_STATUS / PROGRESS / crawl_diff_definitions) はここに載せない。
// 日々の作業の流れから外れるため。URL (/guidelines/<file>.md) では読める。
const DOC_GROUPS = [
  { heading: 'はじめに', items: [
    { file: 'LDB担当者_運用マニュアル.md', note: '毎日の作業はここから' },
  ] },
  { heading: '判断に迷ったら', items: [
    { file: 'APPROVAL_PATTERNS.md', note: '承認する / しないの判例' },
  ] },
  { heading: '担当業務別', items: [
    { file: 'BUDGET_MANUAL.md', note: '予算データの取得' },
    { file: 'LDB_CODE_LINKAGE.md', note: 'LDBコード付与の仕様' },
  ] },
  { heading: '参考', items: [
    { file: 'WEB_APP_MANUAL.md', note: '画面ごとの詳細' },
    { file: 'CHANGELOG.md', note: 'システムの更新内容' },
  ] },
];
// API が返したファイルだけを、DOC_GROUPS の区分と並び順で返す。
// (資料を消した・まだ置いていないときに空の見出しだけ出さない)
const docGroupsFor = (files) => {
  const have = new Set(files || []);
  return DOC_GROUPS
    .map(g => ({ heading: g.heading, items: g.items.filter(it => have.has(it.file)) }))
    .filter(g => g.items.length > 0);
};

// ガイドラインのメニュー本体。区分の見出し + 資料名 + 一行の説明。
// 開いた状態 (indent=40) と畳んだ状態の浮きメニュー (indent=14) で共用する。
function DocsMenuItems({ groups, indent, onPick }) {
  if (!groups || groups.length === 0) {
    return (
      <li style={{ display: 'block', padding: `6px 14px 6px ${indent}px`, color: '#64748b', fontSize: '0.78rem' }}>
        マニュアルがありません
      </li>
    );
  }
  return (
    <>
      {groups.map((g, gi) => (
        <React.Fragment key={g.heading}>
          <li style={{
            display: 'block', padding: `${gi === 0 ? 2 : 8}px 14px 2px ${indent}px`,
            color: '#64748b', fontSize: '0.66rem', letterSpacing: '0.04em',
          }}>
            {g.heading}
          </li>
          {g.items.map(it => (
            <li key={it.file} style={{ display: 'block' }}>
              <Link
                to={`/guidelines/${it.file}`}
                onClick={onPick}
                style={{
                  display: 'block', padding: `4px 14px 5px ${indent}px`,
                  color: '#cbd5e1', textDecoration: 'none', fontSize: '0.8rem',
                }}
              >
                {docTitle(it.file)}
                <span style={{ display: 'block', color: '#64748b', fontSize: '0.68rem', lineHeight: 1.3 }}>
                  {it.note}
                </span>
              </Link>
            </li>
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

function AppContent() {
  const { user, logout } = useAuth();
  // 調査担当 (リモートワーカー) には「アンマッチ調査」だけを見せる。
  // 本当の線引きは API 側 (src/api/auth.py の _enforce_role_scope) で、
  // ここは画面の見た目をそろえるためのもの。
  const isResearcher = Boolean(user) && (user.role || '').toLowerCase() !== 'admin';
  const [docs, setDocs] = useState([]);
  const [xrefEnabled, setXrefEnabled] = useState(false);
  const [isDocsMenuOpen, setIsDocsMenuOpen] = useState(false);
  const docsMenuRef = useRef(null);
  // Close the dropdown when the user clicks anywhere outside of it.
  // Hover used to be the only way to open it, which was fragile (pointer
  // exits the <li> when crossing the gap to the popup), so the parent is
  // now a click-to-toggle button — that requires outside-click handling.
  useEffect(() => {
    if (!isDocsMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (docsMenuRef.current && !docsMenuRef.current.contains(e.target)) {
        setIsDocsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isDocsMenuOpen]);
  // Sidebar collapsed state persists across reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* noop */ }
  }, [sidebarCollapsed]);

  // ログインしてから取りに行く。
  //
  // 依存が [] だと、この画面が立ち上がった一度きりしか呼ばれない。docs は
  // 認証が要るようになったので、ログイン前に一度呼んで失敗すると、その後
  // ログインしてもメニューは空のままになる（画面を読み込み直すまで戻らない）。
  useEffect(() => {
    // 調査担当 (リモートワーカー) にはマニュアルのメニューを出さない
    // (API 側でも止めているので、呼んでも空が返るだけ)。
    if (!user || isResearcher) {
      setDocs([]);
      return;
    }
    const fetchDocs = async () => {
      const files = await getDocsList();
      setDocs(files);
    };
    fetchDocs();
    // 失敗しても画面は動かす (メニューに出さないだけ)
    getXrefStats().then(r => setXrefEnabled(Boolean(r && r.enabled))).catch(() => setXrefEnabled(false));
  }, [user, isResearcher]);

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_OPEN;

  return (
    <div className="App" style={user ? { display: 'flex', minHeight: '100vh' } : undefined}>
      {user && (
        <aside
          style={{
            width: `${sidebarWidth}px`,
            flexShrink: 0,
            background: '#1e293b',
            color: '#e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            top: 0,
            height: '100vh',
            transition: 'width 0.15s',
            overflow: 'hidden',
          }}
        >
          {/* Collapse toggle */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(v => !v)}
            title={sidebarCollapsed ? 'メニューを展開' : 'メニューを最小化'}
            aria-label="メニュー表示切替"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '10px',
              fontSize: '1.2rem',
              textAlign: sidebarCollapsed ? 'center' : 'right',
            }}
          >
            {sidebarCollapsed ? '☰' : '‹'}
          </button>

          {/* Inline overrides cancel the global `nav { background, padding,
              margin-bottom }` and `nav ul { display: flex }` rules from
              App.css — those were written for the old top bar and would
              otherwise force horizontal layout / extra padding here. */}
          <nav style={{
            flex: 1, overflowY: 'auto',
            background: 'transparent', padding: 0, margin: 0,
          }}>
            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
              display: 'block', flexWrap: 'nowrap',
            }}>
              {/* アンマッチ紐付けは「予算」の上に置く (2026-09-24 要望)。
                  末尾に足すとマスタ系の作業メニューから離れてしまうため。 */}
              {(isResearcher
                ? [XREF_RESEARCH_NAV_ITEM]
                : xrefEnabled
                ? insertBefore(
                    insertBefore(
                      insertBefore(
                        insertBefore(NAV_ITEMS, XREF_HOJIN_NAV_ITEM, '/budget'),
                        XREF_STATS_NAV_ITEM, '/xref/hojin'),
                      XREF_RESEARCH_NAV_ITEM, '/xref/makers'),
                    XREF_NAV_ITEM, '/xref/research')
                : NAV_ITEMS).map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    className={({ isActive }) => isActive ? 'sidebar-nav-active' : ''}
                    style={({ isActive }) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: sidebarCollapsed ? '10px 14px' : '10px 16px',
                      color: '#cbd5e1',
                      textDecoration: 'none',
                      borderLeft: isActive ? '3px solid #38bdf8' : '3px solid transparent',
                      background: isActive ? '#0f172a' : 'transparent',
                      fontSize: '0.88rem',
                      whiteSpace: 'nowrap',
                    })}
                  >
                    <span style={{ fontSize: '1.05rem', flexShrink: 0 }} aria-hidden="true">{item.icon}</span>
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
              {/* Guideline dropdown. When the sidebar is expanded, children
                  are rendered INLINE below the parent button (standard tree
                  navigation). When collapsed, the dropdown floats to the
                  right as a fixed-position popup so it isn't clipped by the
                  sidebar's `overflow: hidden`. Previous absolute-positioned
                  popup was being clipped by the aside. */}
              <li
                ref={docsMenuRef}
                style={{ position: 'relative' }}
              >
                <button
                  type="button"
                  title="マニュアル"
                  aria-haspopup="true"
                  aria-expanded={isDocsMenuOpen}
                  onClick={() => setIsDocsMenuOpen(v => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: sidebarCollapsed ? '10px 14px' : '10px 16px',
                    background: isDocsMenuOpen ? '#0f172a' : 'transparent',
                    border: 'none',
                    color: '#cbd5e1',
                    fontSize: '0.88rem',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: '1.05rem', flexShrink: 0 }} aria-hidden="true">📘</span>
                  {!sidebarCollapsed && (
                    <span style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>マニュアル</span>
                      <span style={{ fontSize: '0.7rem', transition: 'transform 0.15s', transform: isDocsMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
                    </span>
                  )}
                </button>
                {/* Inline submenu (expanded sidebar) — does NOT escape the
                    sidebar, so overflow:hidden no longer clips it.
                    `display: block` overrides legacy App.css `nav ul {
                    display: flex }` which would otherwise lay the submenu
                    items horizontally and push them out of view. */}
                {isDocsMenuOpen && !sidebarCollapsed && (
                  <ul style={{
                    display: 'block', flexDirection: 'column',
                    listStyle: 'none', padding: '4px 0 6px 0', margin: 0,
                    background: '#0f172a', borderTop: '1px solid #334155',
                  }}>
                    <DocsMenuItems groups={docGroupsFor(docs)} indent={40}
                                   onPick={() => setIsDocsMenuOpen(false)} />
                  </ul>
                )}
                {/* Floating submenu (collapsed sidebar) — position:fixed so
                    it isn't clipped by aside { overflow: hidden }. Same
                    inline `display: block` defenses against legacy nav ul
                    flex rule. */}
                {isDocsMenuOpen && sidebarCollapsed && (
                  <ul style={{
                    display: 'block', flexDirection: 'column',
                    listStyle: 'none', position: 'fixed',
                    left: `${sidebarWidth + 4}px`, top: '80px',
                    background: '#0f172a', minWidth: '220px', maxHeight: '60vh', overflowY: 'auto',
                    padding: '4px 0', margin: 0, border: '1px solid #334155',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)', zIndex: 1000,
                  }}>
                    <DocsMenuItems groups={docGroupsFor(docs)} indent={14}
                                   onPick={() => setIsDocsMenuOpen(false)} />
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          {/* Footer: user + logout */}
          <div style={{
            borderTop: '1px solid #334155',
            padding: '8px 12px',
            fontSize: '0.74rem',
            color: '#94a3b8',
            display: 'flex',
            flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center',
            gap: '6px',
            justifyContent: 'space-between',
          }}>
            {!sidebarCollapsed && (
              <span title={user.email} style={{
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px',
              }}>{user.email}</span>
            )}
            <button
              onClick={logout}
              title="ログアウト"
              style={{
                background: 'transparent', border: '1px solid #475569',
                color: '#cbd5e1', padding: '2px 8px', borderRadius: '3px',
                cursor: 'pointer', fontSize: '0.72rem',
              }}
            >{sidebarCollapsed ? '⎋' : 'Logout'}</button>
          </div>
        </aside>
      )}

      <main style={user ? { flex: 1, minWidth: 0, overflow: 'auto' } : undefined}>
      <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>読み込み中...</div>}>
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          {/* 旧 /crawls は ホームに統合されたためリダイレクト */}
          <Route path="/crawls"           element={<Navigate to="/" replace />} />
          <Route path="/crawls/schedules" element={<Navigate to="/" replace />} />
          <Route
            path="/crawl-results"
            element={
              <ProtectedRoute>
                <CrawlResultsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare/:kikan_code"
            element={
              <ProtectedRoute>
                <OrgTreeComparePage />
              </ProtectedRoute>
            }
          />
          {/* Legacy redirects — preserve URL search params so deep links from
              older bookmarks / external links keep working. */}
          <Route path="/summary"        element={<Navigate to="/crawl-results" replace />} />
          <Route path="/results"        element={<RedirectWithSearch to="/crawl-results" />} />
          <Route path="/schedules"      element={<Navigate to="/" replace />} />
          <Route path="/schedules/list" element={<Navigate to="/" replace />} />
          <Route
            path="/guidelines/:filename"
            element={
              <ProtectedRoute>
                <ManualPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/url-corrections"
            element={
              <ProtectedRoute>
                <UrlCorrectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/budget"
            element={
              <ProtectedRoute>
                <BudgetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pending-updates"
            element={
              <ProtectedRoute>
                <PendingUpdatesPage />
              </ProtectedRoute>
            }
          />
         {/* /crawl-diffs summary と list は /crawl-results に統合済み。
             field-level diff 詳細だけ専用ページとして残す。 */}
         <Route path="/crawl-diffs"         element={<RedirectWithSearch to="/crawl-results" />} />
         <Route path="/crawl-diffs/summary" element={<Navigate to="/crawl-results" replace />} />
         <Route
           path="/crawl-diffs/:id"
           element={
             <ProtectedRoute>
               <CrawlDiffDetailPage />
             </ProtectedRoute>
           }
         />
         {/* #97: 機関・組織・研究者の検索・参照・編集・CSV入出力 */}
         <Route
           path="/master"
           element={
             <ProtectedRoute>
               <MasterBrowsePage />
             </ProtectedRoute>
           }
         />
         {/* 機関アンマッチ。本体の「機関アンマッチリスト」メールの置き換え。 */}
         <Route
           path="/kikan_unmatch"
           element={
             <ProtectedRoute>
               <KikanUnmatchPage />
             </ProtectedRoute>
           }
         />
         {/* アンマッチ紐付け (LDBコード付与)。夜間に取り込み・AI 提案まで済ませ、
             ここで人が決める。承認分は当日 23:00 に scm-bio.com 側へ書き戻す。 */}
         <Route
           path="/xref"
           element={
             <ProtectedRoute>
               <XrefPage />
             </ProtectedRoute>
           }
         />
         {/* アンマッチ調査 (調査担当 / リモートワーカー)。調べて所見を付けて
             報告するだけの画面で、ここからは紐付けを確定できない。 */}
         <Route
           path="/xref/research"
           element={
             <ProtectedRoute>
               <XrefResearchPage />
             </ProtectedRoute>
           }
         />
         {/* メーカー別の付与実績。作業はせず、台帳の累計と処理状況を見るだけの画面。 */}
         <Route
           path="/xref/makers"
           element={
             <ProtectedRoute>
               <XrefMakerStatsPage />
             </ProtectedRoute>
           }
         />
         {/* 処理3-1 が取り込む受注先法人の切り替え。設定の実体は scm-bio.com 側。 */}
         <Route
           path="/xref/hojin"
           element={
             <ProtectedRoute>
               <XrefTargetHojinPage />
             </ProtectedRoute>
           }
         />
         <Route
           path="/researcher-dedup"
           element={
             <ProtectedRoute>
               <ResearcherDedupPage />
             </ProtectedRoute>
           }
         />
         <Route
           path="/audit-log"
           element={
             <ProtectedRoute>
               <AuditLogPage />
             </ProtectedRoute>
           }
         />
         <Route
           path="/feedback"
           element={
             <ProtectedRoute>
               <FeedbackPage />
             </ProtectedRoute>
           }
         />
        </Routes>
      </Suspense>
      </main>

      {/* Always-on-top, non-modal feedback launcher — lets operators report an
          improvement about the current screen from anywhere (only when signed in). */}
      {/* ご意見・要望は社内の担当者向け。調査担当には出さない (POST も通らない)。 */}
      {user && !isResearcher && <FloatingFeedbackWidget />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
